import React, { useEffect } from 'react';
import { Modal, Form, Input, message, Button, InputNumber } from 'antd';
import { useTranslation } from '@refinedev/core';
import useApi from '@/api/useApi';
import { endPoints } from '@/api/configs';

interface EditTopicModalProps {
  open: boolean;
  onCancel: () => void;
  refetch: () => void;
  topicId: string;
  initialValues: { name: string; sort_order: number };
}

export const EditTopicModal: React.FC<EditTopicModalProps> = ({
  open,
  onCancel,
  refetch,
  topicId,
  initialValues,
}) => {
  const { translate } = useTranslation();
  const { usePatch } = useApi();
  const { mutate: updateTopic, isLoading } = usePatch(endPoints.topic(topicId));
  const [form] = Form.useForm();

  useEffect(() => {
    if (open && initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [open, initialValues, form]);

  const handleOk = async () => {
    const currentValues = form.getFieldsValue();
    const changedValues: { name?: string; sort_order?: number } = {};

    if (currentValues.name !== initialValues.name) {
      changedValues.name = currentValues.name;
    }

    if (currentValues.sort_order !== initialValues.sort_order) {
      changedValues.sort_order = Number(currentValues.sort_order);
    }

    // Only proceed if there are changes
    if (Object.keys(changedValues).length === 0) {
      message.info(translate('listing-topics.no-changes'));
      onCancel();
      return;
    }

    updateTopic(changedValues, {
      onSuccess: () => {
        form.resetFields();
        message.success(translate('listing-topics.success.edit-topic'));
        refetch();
        onCancel();
      },
      onError: (error) => {
        message.error(error.message);
      },
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={translate('listing-topics.edit-topic')}
      open={open}
      footer={null}
      onClose={handleCancel}
      onCancel={handleCancel}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleOk}
        initialValues={initialValues}
      >
        <Form.Item
          name="name"
          label={translate('listing-topics.name')}
          rules={[
            {
              required: true,
              message: translate('pages.errors.required'),
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="sort_order"
          label={translate('listing-topics.sort-order')}
          rules={[
            {
              required: true,
              message: translate('pages.errors.required'),
            },
            {
              validator: (_, value) => {
                if (value < 1) {
                  return Promise.reject(
                    new Error(translate('pages.errors.positive-integer'))
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
          className="w-full"
        >
          <InputNumber min={1} className="w-full" />
        </Form.Item>
        <div className="flex justify-end gap-2">
          <Button onClick={handleCancel}>{translate('buttons.cancel')}</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {translate('buttons.save')}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
